// Single source of truth for all editable site content.
// - Used as the fallback/default when the database has no override.
// - Used to seed the database from the admin panel ("Import current content").
//
// Each Entry maps to one row in the `content_entries` table:
//   (page, section, item_key) is unique. `data` holds the fields.
// A field value is either a plain string/number (not translated) or a
// bilingual { en, fi } object.

import heroBirriaAsset from "@/assets/hero-birria.jpg.asset.json";
import quesabirriaAsset from "@/assets/quesabirria.jpg.asset.json";
import birriaRamenAsset from "@/assets/birria-ramen.jpg.asset.json";
import streetNightAsset from "@/assets/street-night.jpg.asset.json";
import tacoHandsAsset from "@/assets/taco-hands.jpg.asset.json";
import communityAsset from "@/assets/community.jpg.asset.json";
import birriaBurritoAsset from "@/assets/birria-burrito.jpg.asset.json";
import loadedFriesAsset from "@/assets/loaded-fries.jpg.asset.json";
import chiliCrunchAsset from "@/assets/chili-crunch.jpg.asset.json";
import salsaVerdeAsset from "@/assets/salsa-verde.jpg.asset.json";
import cremaAsset from "@/assets/crema.jpg.asset.json";
import grillFlamesAsset from "@/assets/grill-flames.jpg.asset.json";
import tacoyaLogoAsset from "@/assets/tacoya-logo.png.asset.json";
const heroBirria = heroBirriaAsset.url;
const quesabirria = quesabirriaAsset.url;
const birriaRamen = birriaRamenAsset.url;
const streetNight = streetNightAsset.url;
const tacoHands = tacoHandsAsset.url;
const community = communityAsset.url;
const birriaBurrito = birriaBurritoAsset.url;
const loadedFries = loadedFriesAsset.url;
const chiliCrunch = chiliCrunchAsset.url;
const salsaVerde = salsaVerdeAsset.url;
const crema = cremaAsset.url;
const grillFlames = grillFlamesAsset.url;
const tacoyaLogo = tacoyaLogoAsset.url;

import shopProductChiliCrunch from "@/assets/product-chili-crunch.jpg.asset.json";
import shopProductSalsaVerde from "@/assets/product-salsa-verde.jpg.asset.json";
import shopProductYellowHeat from "@/assets/product-yellow-heat.jpg.asset.json";
import shopProductSmokedMorita from "@/assets/product-smoked-morita.jpg.asset.json";
import shopProductSeasoning from "@/assets/product-seasoning.jpg.asset.json";
import shopProductBirriaMix from "@/assets/product-birria-mix.jpg.asset.json";
import shopProductTee from "@/assets/product-tee.jpg.asset.json";
import shopProductHoodie from "@/assets/product-hoodie.jpg.asset.json";


import photoStorefrontWide from "@/assets/photos/storefront-wide.jpg.asset.json";
import photoStorefrontDoor from "@/assets/photos/storefront-door.jpg.asset.json";
import photoNeonSign from "@/assets/photos/neon-sign.jpg.asset.json";
import photoOpenSign from "@/assets/photos/open-sign.jpg.asset.json";
import photoQuesadillaTopdown from "@/assets/photos/quesadilla-topdown.jpg.asset.json";
import photoQuesadillaDip from "@/assets/photos/quesadilla-dip.jpg.asset.json";
import photoTacoCloseup from "@/assets/photos/taco-closeup.jpg.asset.json";
import photoQuesadillaCloseup from "@/assets/photos/quesadilla-closeup.jpg.asset.json";
import photoStorefrontWindow from "@/assets/photos/storefront-window.jpg.asset.json";
import photoBirriaTacosPair from "@/assets/photos/birria-tacos-pair.jpg.asset.json";
import photoSpreadTrays from "@/assets/photos/spread-trays.jpg.asset.json";
import photoTacosCloseup from "@/assets/photos/tacos-closeup.jpg.asset.json";
import photoTacosTray from "@/assets/photos/tacos-tray.jpg.asset.json";
import photoQuesadillaDip2 from "@/assets/photos/quesadilla-dip2.jpg.asset.json";
import photoTortasTray from "@/assets/photos/tortas-tray.jpg.asset.json";
import photoTortasHands from "@/assets/photos/tortas-hands.jpg.asset.json";
import photoTortaCloseup from "@/assets/photos/torta-closeup.jpg.asset.json";

import { translations } from "@/lib/translations";

export type Lang = "EN" | "FI";
export type TField = { en: string; fi: string };
export type FieldValue = TField | string | number | boolean;

export interface Entry {
  page: string;
  section: string;
  key: string;
  order: number;
  data: Record<string, FieldValue>;
  image?: string;
}

/** Bilingual field helper. If fi omitted, defaults to en (admin can translate). */
const t = (en: string, fi?: string): TField => ({ en, fi: fi ?? en });

function isTFieldValue(v: FieldValue): v is TField {
  return typeof v === "object" && v !== null && "en" in v && "fi" in v;
}

/**
 * Fill in missing Finnish from the EN→FI dictionary so the database seed and
 * the live-site fallback are both fully bilingual without hand-editing every
 * field. Only touches fields where Finnish still equals English.
 */
function fillFinnish(entries: Entry[]): Entry[] {
  const dict = translations.FI;
  const lower: Record<string, string> = {};
  for (const k in dict) lower[k.trim().toLowerCase()] = dict[k];
  for (const entry of entries) {
    for (const key in entry.data) {
      const v = entry.data[key];
      if (!isTFieldValue(v)) continue;
      if (v.fi !== v.en) continue;
      const en = v.en.trim();
      if (!en) continue;
      const hit = dict[en] ?? lower[en.toLowerCase()];
      if (hit) v.fi = hit;
    }
  }
  return entries;
}

export const DEFAULT_CONTENT: Entry[] = fillFinnish([
  // ============================ HEADER ============================
  { page: "header", section: "brand", key: "main", order: 0, data: { name: t("TACOYA") } },
  { page: "header", section: "nav", key: "home", order: 0, data: { label: t("Home", "Etusivu"), to: "/" } },
  { page: "header", section: "nav", key: "menu", order: 1, data: { label: t("Menu", "Menu"), to: "/menu" } },
  { page: "header", section: "nav", key: "locations", order: 2, data: { label: t("Locations", "Sijainnit"), to: "/locations" } },
  { page: "header", section: "nav", key: "franchise", order: 3, data: { label: t("Franchise", "Franchising"), to: "/franchise" } },

  // ============================ FOOTER ============================
  {
    page: "footer", section: "brand", key: "main", order: 0,
    data: {
      title: t("TACOYA"),
      blurb: t(
        "The new generation of Nordic street food. Born from the streets of Helsinki. Powered by community. Built to scale.",
        "Pohjoismaisen katuruoan uusi sukupolvi. Syntynyt Helsingin kaduilla. Yhteisön voimalla. Rakennettu skaalautumaan."
      ),
      instagram_url: "https://instagram.com",
      tiktok_url: "https://tiktok.com",
    },
  },
  { page: "footer", section: "explore", key: "heading", order: 0, data: { title: t("Explore", "Tutustu") } },
  { page: "footer", section: "explore", key: "menu", order: 1, data: { label: t("Menu", "Menu"), to: "/menu" } },
  { page: "footer", section: "explore", key: "locations", order: 2, data: { label: t("Locations", "Sijainnit"), to: "/locations" } },
  { page: "footer", section: "explore", key: "franchise", order: 3, data: { label: t("Franchise", "Franchising"), to: "/franchise" } },
  {
    page: "footer", section: "contact", key: "main", order: 0,
    data: {
      title: t("Contact", "Yhteystiedot"),
      line1: t("Lapinlahdenkatu"),
      line2: t("00180 Helsinki"),
      email: "hello@tacoya.fi",
    },
  },
  {
    page: "footer", section: "legal", key: "main", order: 0,
    data: {
      copyright: t("Tacoya. Helsinki, Finland."),
      tagline: t("Modern Mexican Soul · Est. Hietalahti", "Modernia meksikolaista sielua · Est. Hietalahti"),
    },
  },

  // ============================ HOME / HERO ============================
  {
    page: "home", section: "hero", key: "main", order: 0, image: heroBirria,
    data: {
      title_a: t("TAC"),
      title_o: t("O"),
      title_b: t("YA"),
      subtitle_a: t("The New Generation of"),
      subtitle_b: t("Nordic Street Food", "katuruoan uusi sukupolvi"),
      tagline_a: t("Born in Hietalahti."),
      tagline_b: t("Proven in Helsinki.", "Todistettu Helsingissä."),
      tagline_c: t("Ready to Scale."),
      blurb: t("From a beloved taco kiosk to one of Helsinki's most talked-about food concepts."),
      cta1_label: t("Build With Us"),
      cta1_href: "#build",
      cta2_label: t("View Menu", "Katso menu"),
      cta3_label: t("Franchise", "Franchising"),
      badge_a: t("Now operating", "Tarjoilemme nyt"),
      badge_b: t("2 Locations · Helsinki"),
    },
  },

  // ============================ HOME / MARQUEE ============================
  { page: "home", section: "marquee", key: "i1", order: 0, data: { text: t("PROVEN CONCEPT") } },
  { page: "home", section: "marquee", key: "i2", order: 1, data: { text: t("MEDIA RECOGNITION") } },
  { page: "home", section: "marquee", key: "i3", order: 2, data: { text: t("ORGANIC GROWTH") } },
  { page: "home", section: "marquee", key: "i4", order: 3, data: { text: t("TWO LOCATIONS") } },
  { page: "home", section: "marquee", key: "i5", order: 4, data: { text: t("READY TO SCALE") } },
  { page: "home", section: "marquee", key: "i6", order: 5, data: { text: t("HELSINKI") } },

  // ============================ HOME / MARQUEE (BUILD/ACCENT) ============================
  { page: "home", section: "marquee_build", key: "b1", order: 0, data: { text: t("BUILD WITH US") } },
  { page: "home", section: "marquee_build", key: "b2", order: 1, data: { text: t("FRANCHISE OPPORTUNITIES") } },
  { page: "home", section: "marquee_build", key: "b3", order: 2, data: { text: t("BRING TACOYA TO YOUR CITY") } },

  // ============================ HOME / BORN FROM STREETS ============================
  {
    page: "home", section: "born_streets", key: "main", order: 0, image: streetNight,
    data: {
      kicker: t("Born From The Streets", "Syntynyt kaduilla"),
      heading_a: t("Built By The", "RAKENNETTU"),
      heading_b: t("Community.", "YHTEISÖN VOIMIN."),
      badge_year: t("2022"),
      badge_place: t("Hietalahti · HEL"),
      chapter1_label: t("Chapter 01", "Luku 01"),
      chapter1_date: t("2022"),
      chapter1_text: t("t started as a small taco kiosk at Hietalahti market. Long queues, organic hype, and a community that showed up not just for tacos — but for the energy."),
      chapter2_label: t("Chapter 02", "Luku 02"),
      chapter2_date: t("Sep 2024"),
      chapter2_text: t("In September 2024 we opened our first brick-and-mortar in Kamppi, Helsinki — and with it, the first birrieria in the Nordics. No big marketing budget. Just real flavor."),
      caption_title: t("Origin Story", "Alkutarina"),
      seal: tacoyaLogo,
    },
  },
  { page: "home", section: "born_stats", key: "s1", order: 0, data: { value: t("2022"), label: t("Founded", "Perustettu"), sub: t("Hietalahti Market Square") } },
  { page: "home", section: "born_stats", key: "s2", order: 1, data: { value: t("#1"), label: t("Birrieria"), sub: t("First in the Nordics") } },
  { page: "home", section: "born_stats", key: "s3", order: 2, data: { value: t("100%"), label: t("Word of Mouth"), sub: t("Zero paid advertising") } },

  // ============================ HOME / BIRRIERIA ============================
  {
    page: "home", section: "birrieria", key: "main", order: 0, image: grillFlames,
    data: {
      kicker: t("Pohjoismaiden ensimmäinen"),
      line1: t("THE ONLY"),
      line2: t("BIRRIERIA"),
      line3: t("IN FINLAND"),
      blurb: t("Authentic Tijuana-style birria. Slow-cooked for hours. Dipped, sizzled, devoured. A concept that doesn't exist anywhere else in the Nordics."),
      featured_label: t("As featured in"),
    },
  },
  { page: "home", section: "press_carousel", key: "hs", order: 0, data: { outlet: t("Helsingin Sanomat"), date: t("03 / 2024"), kicker: t("Print + Online"), quote: t("Helsingin kuumin uusi street food -ilmiö."), tint: "#d6232a" } },
  { page: "home", section: "press_carousel", key: "city", order: 1, data: { outlet: t("City"), date: t("2024"), kicker: t("Best Tacos · Helsinki"), quote: t("The most authentic birria in the city — hands down."), tint: "var(--tacoya-yellow)", featured: true } },
  { page: "home", section: "press_carousel", key: "google", order: 2, data: { outlet: t("Google"), date: t("275 reviews"), kicker: t("★ 4.6 / 5 · Helsinki"), quote: t("Authentic birria, slow-cooked, worth every minute of the queue."), tint: "#4285F4" } },
  { page: "home", section: "press_carousel", key: "tiktok", order: 3, data: { outlet: t("TikTok"), date: t("@tacoya"), kicker: t("400K+ views"), quote: t("POV: you waited 40 min and it was worth every second."), tint: "#FE2C55" } },
  { page: "home", section: "press_carousel", key: "instagram", order: 4, data: { outlet: t("Instagram"), date: t("@tacoya"), kicker: t("Trending · Reels"), quote: t("The dip shot that broke the algorithm."), tint: "#E1306C" } },

  // ============================ HOME / MEDIA & RECOGNITION ============================
  {
    page: "home", section: "media", key: "main", order: 0,
    data: {
      kicker: t("Media & Recognition"),
      heading_a: t("FEATURED"),
      heading_b: t("IN"),
      blurb: t("Recognition from Finland's leading media — and from a community that keeps talking. Credibility you can't buy."),
    },
  },
  { page: "home", section: "media_items", key: "m1", order: 0, data: { outlet: t("Helsingin Sanomat"), tag: t("Print + Online"), headline: t("Helsingin suosittu tacokioski avasi ravintolan Kamppiin"), accent: "#d6232a" } },
  { page: "home", section: "media_items", key: "m2", order: 1, data: { outlet: t("Helsingin Sanomat"), tag: t("HS Top 10 Restaurants"), headline: t("Helsingin Sanomien Top 10 kiinnostavaa ravintolaa"), accent: "#d6232a" } },
  { page: "home", section: "media_items", key: "m3", order: 2, data: { outlet: t("City Magazine"), tag: t("Best Tacos · Helsinki"), headline: t("Parhaat tacot — City"), accent: "var(--tacoya-yellow)" } },
  { page: "home", section: "media_items", key: "m4", order: 3, data: { outlet: t("Food Influencer Channels"), tag: t("Creator Coverage"), headline: t("Featured across Helsinki's biggest food creators"), accent: "#E1306C" } },
  { page: "home", section: "media_items", key: "m5", order: 4, data: { outlet: t("YouTube Reviews"), tag: t("Long-form Reviews"), headline: t("Independent reviews driving organic discovery"), accent: "#FF0000" } },
  { page: "home", section: "media_items", key: "m6", order: 5, data: { outlet: t("Customer Generated Content"), tag: t("Earned Media"), headline: t("Thousands of guest posts, reels and stories"), accent: "var(--lime)" } },

  // ============================ HOME / SOCIAL PROOF (BY THE NUMBERS) ============================
  {
    page: "home", section: "numbers", key: "main", order: 0,
    data: {
      kicker: t("Social Proof"),
      heading_a: t("TACOYA BY"),
      heading_b: t("THE NUMBERS"),
      blurb: t("Tacoya doesn't rely on paid marketing. The brand spreads organically — driven by flavor, identity and a loyal community."),
    },
  },
  { page: "home", section: "numbers_items", key: "n1", order: 0, data: { value: t("2"), label: t("Locations", "Sijainnit"), sub: t("Operating in Helsinki"), icon: "MapPin" } },
  { page: "home", section: "numbers_items", key: "n2", order: 1, data: { value: t("285+"), label: t("Google Reviews"), sub: t("And climbing"), icon: "Google" } },
  { page: "home", section: "numbers_items", key: "n3", order: 2, data: { value: t("4.6★"), label: t("Original Flagship"), sub: t("Kamppi · Helsinki"), icon: "Star" } },
  { page: "home", section: "numbers_items", key: "n4", order: 3, data: { value: t("5.0★"), label: t("New Unit"), sub: t("Newest location"), icon: "Star" } },
  { page: "home", section: "numbers_items", key: "n5", order: 4, data: { value: t("616,000"), label: t("Organic Views"), sub: t("Zero paid reach"), icon: "TikTok" } },
  { page: "home", section: "numbers_items", key: "n6", order: 5, data: { value: t("20,000"), label: t("Profile Visits"), sub: t("Brand discovery"), icon: "Instagram" } },
  { page: "home", section: "numbers_items", key: "n7", order: 6, data: { value: t("20,000"), label: t("Likes"), sub: t("Across platforms"), icon: "Instagram" } },
  { page: "home", section: "numbers_items", key: "n8", order: 7, data: { value: t("2,023"), label: t("Comments"), sub: t("Real conversations"), icon: "MessageCircle" } },
  { page: "home", section: "numbers_items", key: "n9", order: 8, data: { value: t("9,845"), label: t("Shares"), sub: t("Spread by fans"), icon: "TikTok" } },
  { page: "home", section: "numbers_items", key: "n10", order: 9, data: { value: t("43,000+"), label: t("Content Views"), sub: t("Last 30 days"), icon: "TikTok" } },

  // ============================ HOME / TRAJECTORY (TIMELINE) ============================
  {
    page: "home", section: "trajectory", key: "main", order: 0,
    data: { kicker: t("The Trajectory"), heading_a: t("FROM ONE LOCATION"), heading_b: t("TO A BRAND") },
  },
  { page: "home", section: "trajectory_items", key: "t1", order: 0, data: { year: t("2024"), text: t("Tacoya opens its first restaurant in Helsinki.") } },
  { page: "home", section: "trajectory_items", key: "t2", order: 1, data: { year: t("2024"), text: t("Featured by Helsingin Sanomat.") } },
  { page: "home", section: "trajectory_items", key: "t3", order: 2, data: { year: t("2025"), text: t("Recognized among Helsinki's most interesting restaurants.") } },
  { page: "home", section: "trajectory_items", key: "t4", order: 3, data: { year: t("2026"), text: t("Second operating location opens.") } },
  { page: "home", section: "trajectory_items", key: "t5", order: 4, data: { year: t("Next", "Seuraavaksi"), text: t("Expansion through partnerships, franchising and new markets."), future: true } },

  // ============================ HOME / THE EDGE (WHY TACOYA) ============================
  {
    page: "home", section: "edge", key: "main", order: 0,
    data: {
      kicker: t("The Edge"),
      heading_a: t("WHY"),
      heading_b: t("TACOYA"),
      blurb: t("Seven reasons Tacoya is built to grow — not just to serve."),
    },
  },
  { page: "home", section: "edge_items", key: "e1", order: 0, data: { text: t("Authentic Mexican-inspired flavors"), icon: "Flame" } },
  { page: "home", section: "edge_items", key: "e2", order: 1, data: { text: t("Strong visual identity"), icon: "Sparkles" } },
  { page: "home", section: "edge_items", key: "e3", order: 2, data: { text: t("Cult-like customer loyalty"), icon: "Heart" } },
  { page: "home", section: "edge_items", key: "e4", order: 3, data: { text: t("Media recognition"), icon: "Award" } },
  { page: "home", section: "edge_items", key: "e5", order: 4, data: { text: t("Organic growth"), icon: "TrendingUp" } },
  { page: "home", section: "edge_items", key: "e6", order: 5, data: { text: t("Scalable operating model"), icon: "Building2" } },
  { page: "home", section: "edge_items", key: "e7", order: 6, data: { text: t("Proven multi-location concept"), icon: "MapPin" } },

  // ============================ HOME / BEST SELLERS ============================
  { page: "home", section: "best_sellers", key: "heading", order: 0, data: { heading_a: t("BEST"), heading_b: t("SELLERS"), link_label: t("Full menu", "Koko menu") } },
  { page: "home", section: "best_sellers_items", key: "quesabirria", order: 1, image: quesabirria, data: { name: t("Quesabirria"), spice: 3, tag: t("Best Seller"), desc: t("Crispy cheese-edge tortilla, slow-cooked beef, side of consommé.") } },
  { page: "home", section: "best_sellers_items", key: "birria_ramen", order: 2, image: birriaRamen, data: { name: t("Birria Ramen"), spice: 2, tag: t("Cult Favorite"), desc: t("Two cultures, one bowl. Rich birria broth, ramen noodles, shredded beef.") } },
  { page: "home", section: "best_sellers_items", key: "birria_taco", order: 3, image: tacoHands, data: { name: t("Birria Taco"), spice: 2, tag: t("OG"), desc: t("The one that started it all. Dipped, fried, devoured.") } },

  // ============================ HOME / BUILD WITH US (INVESTOR & PARTNERSHIP) ============================
  {
    page: "home", section: "build", key: "main", order: 0,
    data: {
      kicker: t("Investor & Partnership"),
      heading_a: t("BUILD"),
      heading_b: t("WITH US"),
      blurb: t("Tacoya is actively exploring growth opportunities, strategic partnerships and future expansion."),
      cta1_label: t("Contact Us"),
      cta1_href: "mailto:hello@tacoya.fi?subject=Partnership%20with%20Tacoya",
      cta2_label: t("Franchise Details"),
    },
  },
  { page: "home", section: "build_items", key: "investors", order: 0, data: { title: t("For Investors"), desc: t("Partner in a proven, scalable Nordic food brand with real traction."), icon: "TrendingUp" } },
  { page: "home", section: "build_items", key: "franchise", order: 1, data: { title: t("For Franchise Partners"), desc: t("Bring Tacoya to your city with a tested, repeatable operating model."), icon: "Handshake" } },
  { page: "home", section: "build_items", key: "commercial", order: 2, data: { title: t("For Commercial Partnerships"), desc: t("Collaborate on retail, events, co-branding and new ventures."), icon: "Building2" } },

  // ============================ HOME / COMMUNITY · VOL.01 ============================
  {
    page: "home", section: "community", key: "main", order: 0,
    data: {
      kicker: t("Community · Vol.01"),
      heading_a: t("THIS IS A"),
      heading_b: t("MOVEMENT."),
      blurb: t("Snapshots from the queue, the kitchen, and the curb — shot by the community."),
      hashtag: t("#tacoyahelsinki"),
      hashtag_url: "https://www.instagram.com/tacoya/",
    },
  },
  { page: "home", section: "community_tiles", key: "c1", order: 0, image: photoStorefrontWide.url, data: { caption: t("The corner terrace in full swing") } },
  { page: "home", section: "community_tiles", key: "c2", order: 1, image: photoNeonSign.url, data: { caption: t("Birrieria glow in the window") } },
  { page: "home", section: "community_tiles", key: "c3", order: 2, image: photoQuesadillaDip.url, data: { caption: t("Hands-on, dip-in, no cutlery") } },
  { page: "home", section: "community_tiles", key: "c4", order: 3, image: photoTacoCloseup.url, data: { caption: t("Slow meat, onion, cilantro") } },
  { page: "home", section: "community_tiles", key: "c5", order: 4, image: photoQuesadillaTopdown.url, data: { caption: t("Crispy cheese edges, golden crust") } },
  { page: "home", section: "community_tiles", key: "c6", order: 5, image: photoOpenSign.url, data: { caption: t("Yellow chairs, sunny terrace") } },
  { page: "home", section: "community_tiles", key: "c7", order: 6, image: photoSpreadTrays.url, data: { caption: t("Tacos, birria and quesa side by side") } },
  { page: "home", section: "community_tiles", key: "c8", order: 7, image: photoQuesadillaCloseup.url, data: { caption: t("Birria, melt and crunch") } },
  { page: "home", section: "community_tiles", key: "c9", order: 8, image: photoStorefrontWindow.url, data: { caption: t("Window service on the street") } },
  { page: "home", section: "community_tiles", key: "c10", order: 9, image: photoBirriaTacosPair.url, data: { caption: t("Crispy shells, consommé on the side") } },
  { page: "home", section: "community_tiles", key: "c11", order: 10, image: photoTortasHands.url, data: { caption: t("Golden, pressed and loaded") } },
  { page: "home", section: "community_tiles", key: "c12", order: 11, image: photoTacosTray.url, data: { caption: t("Three on a tray, lime on the side") } },
  { page: "home", section: "community_tiles", key: "c13", order: 12, image: photoQuesadillaDip2.url, data: { caption: t("Straight into the consommé") } },
  { page: "home", section: "community_tiles", key: "c14", order: 13, image: photoTacosCloseup.url, data: { caption: t("Slow-cooked, fresh onion and cilantro") } },
  { page: "home", section: "community_tiles", key: "c15", order: 14, image: photoTortasTray.url, data: { caption: t("Three-cut, sauce on the side") } },
  { page: "home", section: "community_tiles", key: "c16", order: 15, image: photoStorefrontDoor.url, data: { caption: t("Open door on the corner") } },
  { page: "home", section: "community_tiles", key: "c17", order: 16, image: photoTortaCloseup.url, data: { caption: t("Melt, beef and toasted bread") } },

  // ============================ HOME / KEY MESSAGE ============================
  {
    page: "home", section: "key_message", key: "main", order: 0,
    data: { final: t("Tacoya is building a brand.") },
  },
  { page: "home", section: "key_message_lines", key: "l1", order: 0, data: { a: t("One restaurant"), b: t("proves a product.") } },
  { page: "home", section: "key_message_lines", key: "l2", order: 1, data: { a: t("Two successful locations"), b: t("prove a concept.") } },
  { page: "home", section: "key_message_lines", key: "l3", order: 2, data: { a: t("Media recognition"), b: t("proves relevance.") } },

  // ============================ HOME / CTA ============================
  {
    page: "home", section: "cta", key: "main", order: 0, image: community,
    data: {
      kicker: t("Helsinki · Kamppi Flagship"),
      heading_a: t("COME EAT"),
      heading_b: t("WITH US."),
      blurb: t("Walk in, queue up, dip in. Authentic Tijuana-style birria — slow-cooked, dipped and sizzled to order. Or bring Tacoya to your city."),
      address_label: t("Address", "Osoite"),
      address: t("Lapinlahdenkatu"),
      address_url: "https://maps.google.com/?q=Lapinlahdenkatu+Helsinki",
      hours_label: t("Open Now", "Auki nyt"),
      hours: t("Mon–Sat 11–22 · Sun 12–21"),
      cta1_label: t("Find Us", "Löydä meidät"),
      cta2_label: t("Franchise", "Franchising"),
      rating: t("4.6"),
      reviews_label: t("· 275 Google reviews"),
      reviews_url: "https://share.google/zDx6qgDwIgmX4NdFx",
    },
  },

  // ============================ MENU ============================
  {
    page: "menu", section: "hero", key: "main", order: 0,
    data: {
      kicker: t("The Menu"),
      heading_a: t("EAT"),
      heading_b: t("LIKE"),
      heading_c: t("THE STREETS."),
      blurb: t("Small menu. Big flavor. Every dish is built on slow-cooked birria and the recipes that put us on the map."),
    },
  },
  // Menu categories
  { page: "menu", section: "categories", key: "birria_original", order: 0, data: { title: t("Birria — The Original") } },
  { page: "menu", section: "categories", key: "fusion", order: 1, data: { title: t("The Fusion") } },
  { page: "menu", section: "categories", key: "sauces", order: 2, data: { title: t("Signature Sauces") } },
  // Menu items — Birria
  { page: "menu", section: "items", key: "birria_taco", order: 0, image: tacoHands, data: { category: "birria_original", name: t("Birria Taco"), price: "5,90", spice: 2, tag: t("OG"), desc: t("Hand-dipped tortilla, slow-cooked beef, white onion, cilantro. Served with consommé.") } },
  { page: "menu", section: "items", key: "quesabirria", order: 1, image: quesabirria, data: { category: "birria_original", name: t("Quesabirria"), price: "7,50", spice: 3, tag: t("Best Seller"), desc: t("Crispy cheese-edge tortilla, melted Oaxaca-style cheese, shredded beef.") } },
  { page: "menu", section: "items", key: "birria_plate", order: 2, image: heroBirria, data: { category: "birria_original", name: t("Birria Plate"), price: "16,90", spice: 2, tag: t(""), desc: t("Stewed beef, consommé bowl, rice, beans and four hand-pressed tortillas.") } },
  // Menu items — Fusion
  { page: "menu", section: "items", key: "birria_ramen", order: 3, image: birriaRamen, data: { category: "fusion", name: t("Birria Ramen"), price: "14,50", spice: 2, tag: t("Cult Favorite"), desc: t("Birria broth, ramen noodles, shredded beef, soft egg, chili oil.") } },
  { page: "menu", section: "items", key: "birria_burrito", order: 4, image: birriaBurrito, data: { category: "fusion", name: t("Birria Burrito"), price: "12,90", spice: 2, tag: t(""), desc: t("Loaded with birria, rice, beans, cheese and salsa verde.") } },
  { page: "menu", section: "items", key: "loaded_fries", order: 5, image: loadedFries, data: { category: "fusion", name: t("Loaded Fries"), price: "9,90", spice: 1, tag: t(""), desc: t("Crispy fries, birria, melted cheese, pickled jalapeños, crema.") } },
  // Menu items — Sauces
  { page: "menu", section: "items", key: "chili_crunch", order: 6, image: chiliCrunch, data: { category: "sauces", name: t("Chili Crunch"), price: "3,50", spice: 3, tag: t(""), desc: t("Smoky, crunchy, dangerously good.") } },
  { page: "menu", section: "items", key: "salsa_verde", order: 7, image: salsaVerde, data: { category: "sauces", name: t("Salsa Verde"), price: "3,00", spice: 2, tag: t(""), desc: t("Roasted tomatillos, jalapeño, lime.") } },
  { page: "menu", section: "items", key: "crema", order: 8, image: crema, data: { category: "sauces", name: t("Crema"), price: "2,50", spice: 0, tag: t(""), desc: t("Cooling Mexican-style crema.") } },

  // ============================ LOCATIONS ============================
  {
    page: "locations", section: "hero", key: "main", order: 0,
    data: { kicker: t("Find Us", "Löydä meidät"), heading_a: t("WHERE"), heading_b: t("TO EAT.") },
  },
  { page: "locations", section: "items", key: "kamppi", order: 0, image: community, data: { name: t("Kamppi Flagship"), address: t("Lapinlahdenkatu, 00180 Helsinki"), hours: t("Mon–Sat 11–22 · Sun 12–21"), vibe: t("Hip-hop · Reggaetón · Latin trap"), status: "OPEN", tag: t("Birrieria") } },
  { page: "locations", section: "items", key: "vaasankatu", order: 1, image: streetNight, data: { name: t("Vaasankatu — Street"), address: t("Vaasankatu, 00500 Helsinki"), hours: t("Coming 2025", "Tulossa 2025"), vibe: t("Late-night street energy"), status: "SOON", tag: t("Street Food Unit") } },
  {
    page: "locations", section: "next_stops", key: "main", order: 0,
    data: {
      kicker: t("Next Stops"),
      cities: t("Tampere · Turku · Stockholm · Oslo"),
      blurb: t("The expansion is happening. Want to bring Tacoya to your city?"),
      cta_label: t("Bring Tacoya here"),
      cta_href: "/franchise",
    },
  },

  // ============================ FRANCHISE ============================
  {
    page: "franchise", section: "hero", key: "main", order: 0, image: grillFlames,
    data: {
      heading_a: t("BRING TACOYA"),
      heading_b: t("TO YOUR CITY."),
      blurb: t("Small footprint. Big brand. Viral product. Operationally simple. Tacoya is built to scale across the Nordics — and beyond."),
      cta_label: t("Start the conversation"),
      cta_href: "#apply",
      card_badge: t("Now opening"),
      card_title: t("2026 Franchise Cohort"),
      card_blurb: t("Limited partners. Hand-picked cities. Personal onboarding with the founders."),
      card_apply_label: t("Apply by"),
      card_apply_value: t("Q2 2026"),
    },
  },
  { page: "franchise", section: "hero_stats", key: "h1", order: 0, data: { value: t("5"), label: t("Year plan") } },
  { page: "franchise", section: "hero_stats", key: "h2", order: 1, data: { value: t("3"), label: t("Nordic markets") } },
  { page: "franchise", section: "hero_stats", key: "h3", order: 2, data: { value: t("1"), label: t("Cohort / 2026") } },
  {
    page: "franchise", section: "pillars", key: "heading", order: 0,
    data: { heading_a: t("WHY"), heading_b: t("TACOYA.") },
  },
  { page: "franchise", section: "pillars_items", key: "compact", order: 0, data: { icon: "box", title: t("Compact Format"), desc: t("High-throughput units that fit anywhere — kiosks, food halls, high streets.") } },
  { page: "franchise", section: "pillars_items", key: "viral", order: 1, data: { icon: "chart-user", title: t("Viral Demand"), desc: t("Hundreds of thousands of organic views. A brand the internet already loves.") } },
  { page: "franchise", section: "pillars_items", key: "ops", order: 2, data: { icon: "bolt", title: t("Simple Ops"), desc: t("Tight menu, slow-cook system, repeatable execution. Built by operators, for operators.") } },
  { page: "franchise", section: "pillars_items", key: "nordic", order: 3, data: { icon: "earth", title: t("Nordic Vision"), desc: t("First-mover in birria across the Nordics. International scale on the roadmap.") } },
  {
    page: "franchise", section: "roadmap", key: "heading", order: 0,
    data: { kicker: t("Roadmap · 2022 → 2027"), heading_a: t("THE"), heading_b: t("EXPANSION.") },
  },
  { page: "franchise", section: "roadmap_items", key: "r1", order: 0, data: { year: t("2022"), title: t("Hietalahti Origins"), desc: t("Tacoya is born as a market kiosk. The queue forms."), status: "done" } },
  { page: "franchise", section: "roadmap_items", key: "r2", order: 1, data: { year: t("2024"), title: t("Kamppi Flagship"), desc: t("First brick-and-mortar opens. Pohjoismaiden ensimmäinen birrieria."), status: "done" } },
  { page: "franchise", section: "roadmap_items", key: "r3", order: 2, data: { year: t("2025"), title: t("Street Food Units"), desc: t("Compact Tacoya Street units roll out across Helsinki."), status: "active" } },
  { page: "franchise", section: "roadmap_items", key: "r4", order: 3, data: { year: t("2026"), title: t("Nordic Expansion"), desc: t("Franchise partners across Finland, Sweden, Norway."), status: "next" } },
  { page: "franchise", section: "roadmap_items", key: "r5", order: 4, data: { year: t("2027+"), title: t("Retail & Beyond"), desc: t("FMCG sauces, supermarket distribution, international scale."), status: "next" } },
  {
    page: "franchise", section: "form", key: "main", order: 0,
    data: {
      kicker: t("Apply · 2026 Cohort"),
      heading_a: t("TELL US ABOUT"),
      heading_b: t("YOUR CITY."),
      blurb: t("Three fields, one ambition. Tell us where you want to bring Tacoya and a little about you. We read every application personally."),
      bullet1: t("Reply within 5 business days"),
      bullet2: t("Discovery call with the founders"),
      bullet3: t("Site visit · Helsinki HQ"),
      field_name: t("Your name"),
      field_email: t("Email"),
      field_city: t("Target city"),
      field_message: t("Tell us about your background"),
      consent: t("By submitting, you agree to be contacted about Tacoya partnerships."),
      submit_label: t("Submit application"),
      success_title: t("Message received."),
      success_text: t("We'll be in touch within a few days. Get ready."),
    },
  },

  // ============================ SHOP ============================
  {
    page: "shop", section: "hero", key: "main", order: 0,
    data: {
      status_a: t("Drop 001 · Loading"),
      status_b: t("Early access open"),
      headline_a: t("TASTE"),
      headline_b: t("TACOYA"),
      headline_c: t("AT HOME."),
      copy: t("The first Tacoya drop. Sauces bottled from our kitchen, spice blends used behind our line, and apparel built like the food — heavy, honest, made to last. Limited first run."),
      countdown_at: "2026-06-05T18:00:00Z",
      launch_label: t("June 5 · 21:00"),
      launch_sub: t("Helsinki time · 1500 units"),
      trust_text: t("1,200+ on the waitlist"),
    },
  },
  { page: "shop", section: "products", key: "chili-crunch", order: 0, image: shopProductChiliCrunch.url, data: { name: t("Chili Crunch No.1"), cat: "Sauce", price: "12 €", heat: 3, color: "var(--chili)", tag: t("Hero"), desc: t("Toasted chiles, garlic, sesame. Drizzle on everything.") } },
  { page: "shop", section: "products", key: "salsa-verde", order: 1, image: shopProductSalsaVerde.url, data: { name: t("Salsa Verde Cruda"), cat: "Sauce", price: "10 €", heat: 2, color: "var(--lime)", desc: t("Tomatillo, jalapeño, cilantro. Bright and loud.") } },
  { page: "shop", section: "products", key: "yellow-heat", order: 2, image: shopProductYellowHeat.url, data: { name: t("Yellow Heat"), cat: "Sauce", price: "11 €", heat: 2, color: "var(--tacoya-yellow)", tag: t("Signature"), desc: t("Habanero, mustard, lime. The Tacoya yellow.") } },
  { page: "shop", section: "products", key: "smoked-morita", order: 3, image: shopProductSmokedMorita.url, data: { name: t("Smoked Morita"), cat: "Sauce", price: "12 €", heat: 3, color: "var(--charcoal)", desc: t("Slow-smoked morita chiles. Deep, almost sweet.") } },
  { page: "shop", section: "products", key: "seasoning", order: 4, image: shopProductSeasoning.url, data: { name: t("Tacoya Seasoning"), cat: "Spice", price: "9 €", heat: 1, color: "var(--chili)", desc: t("House blend. The one we use behind the line.") } },
  { page: "shop", section: "products", key: "birria-mix", order: 5, image: shopProductBirriaMix.url, data: { name: t("Birria Spice Mix"), cat: "Spice", price: "14 €", heat: 2, color: "var(--charcoal)", tag: t("Limited"), desc: t("Make our birria at home. 4-portion pack.") } },
  { page: "shop", section: "products", key: "tee", order: 6, image: shopProductTee.url, data: { name: t("Logo Tee — Black"), cat: "Apparel", price: "39 €", heat: 0, color: "var(--charcoal)", desc: t("Heavy 240gsm cotton. Front mark, back wordmark.") } },
  { page: "shop", section: "products", key: "hoodie", order: 7, image: shopProductHoodie.url, data: { name: t("Hietalahti Hoodie"), cat: "Apparel", price: "79 €", heat: 0, color: "var(--tacoya-yellow)", tag: t("Drop"), desc: t("Tribute to where it started. 400gsm fleece.") } },
  {
    page: "shop", section: "notify", key: "main", order: 0,
    data: {
      badge: t("Early access"),
      title_a: t("GET FIRST"),
      title_b: t("DIBS."),
      copy: t("24-hour head start. Drop alerts. Behind-the-line stories. No spam, ever."),
      members: t("2.4k members"),
      frequency: t("Avg 1 email / drop"),
      placeholder: t("your@email.com"),
      submit_label: t("Notify me →"),
      disclaimer: t("By signing up you agree to occasional drop emails."),
      success_title: t("You're on the list."),
      success_text: t("Check your inbox — we'll ping you 24 hours before public drop."),
    },
  },

  // ============================ SHOP / FEATURED ============================
  {
    page: "shop", section: "featured", key: "main", order: 0, image: quesabirria,
    data: {
      kicker: t("Drop hero"),
      title: t("CHILI CRUNCH No.1"),
      right: t("The bottle that started the queue."),
      alt: t("Chili Crunch No.1"),
    },
  },

  // ============================ SHOP / LOOKBOOK ============================
  {
    page: "shop", section: "lookbook", key: "main", order: 0,
    data: {
      kicker: t("Lookbook 001"),
      title: t("FOOD. STREET. UNIFORM."),
    },
  },
  { page: "shop", section: "lookbook_tiles", key: "l1", order: 0, image: community, data: { alt: t("Tacoya community gathered at a Helsinki pop-up") } },
  { page: "shop", section: "lookbook_tiles", key: "l2", order: 1, image: heroBirria, data: { alt: t("Birria taco dipped in rich consommé") } },
  { page: "shop", section: "lookbook_tiles", key: "l3", order: 2, image: quesabirria, data: { alt: t("Cheesy quesabirria fresh off the grill") } },
  { page: "shop", section: "lookbook_tiles", key: "l4", order: 3, image: streetNight, data: { alt: t("Tacoya street stall lit up at night") } },
]);

